/**
 * Shared Brevo transactional email helpers.
 * All notification emails (Strategy Call, Website Request, etc.) go through here.
 */

const BREVO_API_URL = 'https://api.brevo.com/v3/smtp/email';
const BREVO_CONTACTS_URL = 'https://api.brevo.com/v3/contacts';

export const DEFAULT_BREVO_SENDER_NAME = 'SMBPrime';
export const DEFAULT_BREVO_SENDER_EMAIL = 'noreply@smbprime.ai';

/** Parse comma- or newline-separated email addresses; trims and filters empty values. */
export function parseNotificationEmails(value: string): string[] {
  return value
    .split(/[\n,]+/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.length > 0);
}

export interface SendBrevoParams {
  apiKey: string;
  toEmails: string[];
  senderName?: string;
  senderEmail?: string;
  subject: string;
  htmlContent: string;
}

/**
 * Send a transactional email via the Brevo REST API.
 * No-ops silently when `toEmails` is empty.
 */
export async function sendBrevoEmail(params: SendBrevoParams): Promise<void> {
  if (params.toEmails.length === 0) return;

  const res = await fetch(BREVO_API_URL, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'api-key': params.apiKey,
    },
    body: JSON.stringify({
      sender: {
        name: (params.senderName ?? '').trim() || DEFAULT_BREVO_SENDER_NAME,
        email: (params.senderEmail ?? '').trim() || DEFAULT_BREVO_SENDER_EMAIL,
      },
      to: params.toEmails.map((email) => ({ email })),
      subject: params.subject,
      htmlContent: params.htmlContent,
    }),
  });

  if (!res.ok) {
    const err = await res.text();
    if (res.status === 401 && err.includes('unrecognised IP')) {
      throw new Error(
        '[Brevo] Unauthorised: your IP is not in Brevo’s authorised list. Add it at https://app.brevo.com/security/authorised-ips or disable IP restriction in Brevo Security.'
      );
    }
    throw new Error(`[Brevo] API error ${res.status}: ${err}`);
  }
}

export interface AddBrevoContactParams {
  apiKey: string;
  email: string;
  firstName?: string | null;
  /** Brevo list IDs to add the contact to (numeric). */
  listIds?: number[];
  /** Additional contact attributes to set (e.g. { COMPANY: 'Acme' }). */
  attributes?: Record<string, string>;
}

/**
 * Add or update a contact in Brevo and optionally add them to contact lists.
 * Uses POST /v3/contacts (creates or updates existing contact).
 * No-ops silently when `email` is empty.
 */
export async function addBrevoContact(params: AddBrevoContactParams): Promise<void> {
  if (!params.email.trim()) return;

  const attributes: Record<string, string> = { ...params.attributes };
  if (params.firstName) {
    attributes['FNAME'] = params.firstName;
  }

  const body: Record<string, unknown> = {
    email: params.email.trim().toLowerCase(),
    updateEnabled: true,
  };

  if (Object.keys(attributes).length > 0) {
    body['attributes'] = attributes;
  }

  const listIds = params.listIds?.filter((id) => Number.isInteger(id) && id > 0) ?? [];
  if (listIds.length > 0) {
    body['listIds'] = listIds;
  }

  const res = await fetch(BREVO_CONTACTS_URL, {
    method: 'POST',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      'api-key': params.apiKey,
    },
    body: JSON.stringify(body),
  });

  const responseText = await res.text();

  if (!res.ok) {
    throw new Error(`[Brevo Contacts] API error ${res.status}: ${responseText}`);
  }
}
